import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from '@noor/lib/cn';
import { Badge } from './badge';

/**
 * A single conversation row in an inbox / chat list. Purely presentational —
 * the caller composes the `avatar` node (e.g. an `<Avatar>` with an
 * `<AvatarStatus>` presence dot) and passes it in. Layout is a three-column
 * flex: avatar · two-line middle (name + preview) · right rail (time + unread).
 *
 * Selected rows take a soft sidebar tint plus a left accent bar so the active
 * conversation reads out of the list at a glance.
 */
const chatListItemVariants = cva(
  'group/chat-item relative flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors outline-none hover:bg-muted focus-visible:ring-ring/40 focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      selected: {
        // A soft brand-tinted row (matches the Figma), no left accent bar.
        true: 'bg-sidebar-accent hover:bg-sidebar-accent',
        false: '',
      },
    },
    defaultVariants: {
      selected: false,
    },
  },
);

type ChatListItemProps = {
  avatar: React.ReactNode;
  name: React.ReactNode;
  preview?: React.ReactNode;
  /** Extra content under the preview line (tags, badges, …). */
  footer?: React.ReactNode;
  time?: React.ReactNode;
  unreadCount?: number;
  selected?: boolean;
  asChild?: boolean;
} & Omit<React.ComponentProps<'button'>, 'children'>;

function ChatListItem({
  className,
  avatar,
  name,
  preview,
  footer,
  time,
  unreadCount,
  selected = false,
  asChild = false,
  ...props
}: ChatListItemProps) {
  const Comp = asChild ? Slot : 'button';
  const hasUnread = unreadCount !== undefined && unreadCount > 0;

  return (
    <Comp
      data-slot="chat-list-item"
      data-selected={selected}
      className={cn(chatListItemVariants({ selected }), className)}
      {...props}
    >
      {avatar}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span
          className={cn(
            'truncate text-sm font-medium tracking-tight text-foreground',
            selected && 'text-sidebar-accent-foreground',
          )}
        >
          {name}
        </span>
        {preview !== undefined ? (
          <span className="truncate text-xs text-muted-foreground">{preview}</span>
        ) : null}
        {footer !== undefined ? <div className="min-w-0 pt-0.5">{footer}</div> : null}
      </div>
      {time !== undefined || hasUnread ? (
        <div className="flex flex-col items-end gap-1">
          {time !== undefined ? (
            <span className="text-xs text-muted-foreground">{time}</span>
          ) : null}
          {hasUnread ? (
            <Badge variant="success" shape="count">
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          ) : null}
        </div>
      ) : null}
    </Comp>
  );
}

export { ChatListItem, chatListItemVariants };
export type { ChatListItemProps };
